import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Box,
  Divider,
  CircularProgress,
  useTheme,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { showErrorToast } from "../../../components/common/ShowToast";

interface NewChatModalProps {
  open: boolean;
  onClose: () => void;
  onStartChat: (phone: string, name?: string) => void;
}

export const NewChatModal: React.FC<NewChatModalProps> = ({
  open,
  onClose,
  onStartChat,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let cleaned = phone.replace(/[^\d+]/g, "").replace(/^\+/, "");
    // Normalize local Zimbabwe numbers (e.g. 077... -> 26377..., or 77... -> 26377...)
    if (cleaned.startsWith("0")) {
      cleaned = "263" + cleaned.slice(1);
    } else if (cleaned.length === 9 && !cleaned.startsWith("263")) {
      cleaned = "263" + cleaned;
    }

    if (!cleaned || cleaned.length < 8) {
      showErrorToast("Please enter a valid phone number with country code (e.g. 263771234567).");
      return;
    }

    onStartChat(cleaned, name.trim());
    setPhone("");
    setName("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: isDark ? "#111827" : "#FFFFFF",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #E2E8F0",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          color: isDark ? "#FFFFFF" : "#0F172A",
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: "rgba(37, 211, 102, 0.15)",
                color: "#25D366",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PersonAddIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
                Start New WhatsApp Chat
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Open a conversation thread with any number
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <Divider sx={{ opacity: 0.15 }} />

        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Recipient Phone Number"
              placeholder="+263 77 123 4567 or 0771234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              helperText="E.164 format with country code (e.g. 263...)"
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Contact / Customer Name (Optional)"
              placeholder="e.g. John Moyo"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Stack>
        </DialogContent>

        <Divider sx={{ opacity: 0.15 }} />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} sx={{ color: "text.secondary" }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<WhatsAppIcon />}
            sx={{
              backgroundColor: "#724E41",
              "&:hover": { backgroundColor: "#5d3e33" },
              fontWeight: 700,
              borderRadius: "10px",
              px: 2.5,
            }}
          >
            Open Chat
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
